import { Text, Heading } from "@otrium/atoms";
import { FC } from "react";
import { useTheme, Theme } from "@emotion/react";
import {
  FaceIconsWrapper,
  ScoreHeader,
  StyledHyperLink,
} from "src/molecules/GoodOnYou/GoodOnYou.styled";
import { Trans } from "@lingui/macro";
import FaceFrownIcon from "src/icons/FaceFrownIcon";
import FaceNeutralIcon from "src/icons/FaceNeutralIcon";
import FaceContentIcon from "src/icons/FaceContentIcon";
import FaceSmileIcon from "src/icons/FaceSmileIcon";
import FaceHappyIcon from "src/icons/FaceHappyIcon";
import { getHowWeRateLink } from "src/molecules/GoodOnYou/utils";

import { locale } from "src/config/app-config";
import { useSegment } from "src/hooks/useSegment";
import { Box } from "@otrium/core";

interface Props {
  scoreMeta: string;
  overallScore: number;
  withSmallHeadings?: boolean;
  pageType: "pdp" | "brandsPage";
  brandName: string;
}

const GoodOnYouRating: FC<Props> = ({
  scoreMeta,
  overallScore,
  withSmallHeadings,
  pageType,
  brandName,
}) => {
  const theme: Theme = useTheme();
  const { segmentBrandConsciousHowWeRateClicked } = useSegment();
  const overallScoreLabel = scoreMeta.split("||")[1];
  const getStrokeColor = (score: number): string =>
    overallScore === score ? theme.colors.tone.black : theme.colors.tone.silver;

  return (
    <Box mb={3}>
      <ScoreHeader>
        <Heading
          as="h5"
          variant={
            withSmallHeadings
              ? "body.source.semibold14"
              : "body.source.semibold16"
          }
          fontSize={
            withSmallHeadings
              ? theme.fontSizes.fontSize14
              : theme.fontSizes.fontSize16
          }
          pb={0}
          pr={3}
          mb={2}
        >
          <Trans>Overall rating is</Trans> &lsquo;{overallScoreLabel}&rsquo;
        </Heading>
        <FaceIconsWrapper mb={2}>
          <FaceFrownIcon stroke={getStrokeColor(1)} />
          <FaceNeutralIcon stroke={getStrokeColor(2)} />
          <FaceContentIcon stroke={getStrokeColor(3)} />
          <FaceSmileIcon stroke={getStrokeColor(4)} />
          <FaceHappyIcon stroke={getStrokeColor(5)} />
        </FaceIconsWrapper>
      </ScoreHeader>
      <Text variant="body.source.regular16" as="p">
        {withSmallHeadings ? (
          <Trans>
            Good On You® ratings are based on a scale from 1/5 (We Avoid) to 5/5
            (Great). These are made up of three overarching areas: planet,
            people and animals.
          </Trans>
        ) : (
          <Trans>
            Most brands on our platform are rated through Good On You®. These
            ratings are based on a scale from 1/5 (We Avoid) to 5/5 (Great).
            &quot;Ethically&quot; qualified items are received from brands with
            a rating of 3/5 (It&apos;s a start), 4/5 (Good) or 5/5 (Great).
          </Trans>
        )}{" "}
        <StyledHyperLink
          color={theme.colors.tone.anthracite}
          asLink
          href={getHowWeRateLink(locale)}
          variant="inherit"
          target="_blank"
          onClick={() =>
            void segmentBrandConsciousHowWeRateClicked({ pageType, brandName })
          }
        >
          <Trans>Click here</Trans>
        </StyledHyperLink>{" "}
        <Trans>
          for further information on how the Good On You® ratings work.
        </Trans>
      </Text>
    </Box>
  );
};

export default GoodOnYouRating;
